import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isProTier } from '@/lib/tier';
import { Seo } from '@/components/Seo';
import { WindForecastChart, HistoryChart } from '@/components/WindCharts';
import { resolveUnits, windUnit, displayWind } from '@/lib/units';
import { SpotGearAdvice } from '@/components/SpotGearAdvice';
import { MyGearMatch } from '@/components/MyGearMatch';
import { WindAlertCard } from '@/components/WindAlertCard';
import {
  MapPin, Wind, Waves, AlertTriangle, Loader2, CalendarDays, Sparkles,
  Home, ShoppingBag, ExternalLink, Sun, Lock, Navigation, Activity, ChevronRight,
} from 'lucide-react';

const WIND_DIRS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
const degToCompass = (deg) => (deg == null ? '' : WIND_DIRS[Math.round(deg / 22.5) % 16]);

export default function SpotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isPro = isProTier(user?.subscription_tier);
  const [spot, setSpot] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [history, setHistory] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const loadSpot = async () => {
      setLoading(true);
      window.scrollTo(0, 0);
      const [s, f, h, all] = await Promise.allSettled([
        api.get(`/spots/${id}`),
        api.get(`/spots/${id}/forecast?days=14`),
        api.get(`/spots/${id}/history`),
        api.get('/spots'),
      ]);
      if (!active) return;
      if (s.status === 'fulfilled') setSpot(s.value.data);
      if (f.status === 'fulfilled') setForecast(f.value.data);
      if (h.status === 'fulfilled') setHistory(h.value.data);
      if (s.status === 'fulfilled' && all.status === 'fulfilled') {
        const cur = s.value.data;
        const list = (all.value.data || [])
          .filter((x) => x.id !== cur.id && (x.country === cur.country || x.type === cur.type))
          .sort((a, b) => (a.country === cur.country ? -1 : 1) - (b.country === cur.country ? -1 : 1))
          .slice(0, 4);
        setRelated(list);
      }
      setLoading(false);
    };
    loadSpot();
    return () => { active = false; };
  }, [id]);

  // Live "now" conditions: hourly entry closest to the current time.
  const nowWind = useMemo(() => {
    const hrs = forecast?.hourly || [];
    if (!hrs.length) return null;
    const now = Date.now();
    let best = hrs[0], bestDiff = Infinity;
    for (const hh of hrs) {
      const d = Math.abs(new Date(hh.time).getTime() - now);
      if (d < bestDiff) { bestDiff = d; best = hh; }
    }
    return best;
  }, [forecast]);

  if (loading || !spot) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#00B4D8] animate-spin" /></div>;

  const locale = (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2);
  const typeLabel = t(`spotDetail.types.${spot.type}`, { defaultValue: spot.type });

  // unit system (knots canonical -> user preference)
  const u = resolveUnits(user);
  const wu = windUnit(u);

  // build chart data: next 24h hourly + daily (converted to preferred wind unit)
  const hourly = (forecast?.hourly || []).slice(0, 24).map((h) => ({ label: h.time.slice(11, 16), wind: displayWind(h.wind, u, 1), gust: displayWind(h.gust, u, 1) }));
  const daily = forecast?.daily || [];
  const historyMonthly = (history?.monthly || []).map((m) => ({ ...m, avg_wind: displayWind(m.avg_wind, u, 1) }));

  // ---------- SEO ----------
  const canonical = typeof window !== 'undefined' ? `${window.location.origin}/spots/${spot.id}` : undefined;
  const seoTitle = `${spot.name} kitesurf spot — wind & verwachting (${spot.country})`;
  const seoDesc = (spot.description || `Kitesurfen in ${spot.name}, ${spot.country}: live wind, 14-daagse verwachting, beste seizoen, faciliteiten en gear-verhuur.`).slice(0, 155);
  const bestMonthsLabels = history?.best_months_labels || [];
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'TouristAttraction',
        name: `${spot.name} Kitesurf Spot`,
        description: seoDesc,
        image: spot.photos?.[0],
        url: canonical,
        address: { '@type': 'PostalAddress', addressRegion: spot.region, addressCountry: spot.country },
        geo: { '@type': 'GeoCoordinates', latitude: spot.gps_lat, longitude: spot.gps_lng },
        touristType: 'Kitesurfers',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Spots', item: typeof window !== 'undefined' ? `${window.location.origin}/spots` : undefined },
          { '@type': 'ListItem', position: 2, name: spot.country },
          { '@type': 'ListItem', position: 3, name: spot.name, item: canonical },
        ],
      },
    ],
  };

  // live "now" wind in preferred unit + kiteability
  const nowVal = nowWind ? displayWind(nowWind.wind, u) : null;
  const nowGust = nowWind ? displayWind(nowWind.gust, u) : null;
  const nowKn = nowWind?.wind ?? null; // knots (canonical)
  const kiteable = nowKn != null && nowKn >= 12 && nowKn <= 35;

  return (
    <div>
      <Seo title={seoTitle} description={seoDesc} canonical={canonical} image={spot.photos?.[0]} type="article" jsonLd={jsonLd} />
      {/* HERO */}
      <section className="relative h-[44vh] min-h-[320px] noise-overlay overflow-hidden">
        <img src={spot.photos?.[0]} alt={`Kitesurfen in ${spot.name}, ${spot.country}`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-full flex flex-col justify-end pb-8">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-white/70 mb-3 flex-wrap" data-testid="spot-breadcrumb">
            <Link to="/spots" className="hover:text-white">{t('spotDetail.breadcrumbSpots')}</Link>
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
            <span className="text-white/85">{spot.country}</span>
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
            <span className="text-white">{spot.name}</span>
          </nav>
          <h1 className="font-display text-5xl sm:text-7xl">{spot.name}</h1>
          <div className="flex items-center gap-4 mt-2 text-white/85 flex-wrap">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {spot.region}, {spot.country}</span>
            <span className="flex items-center gap-1"><Waves className="w-4 h-4 text-[#48CAE4]" /> {typeLabel}</span>
            <span className="flex items-center gap-1 capitalize"><Sparkles className="w-4 h-4 text-[#FFB020]" /> {spot.level === 'all' ? t('spotDetail.allLevels') : spot.level}</span>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 grid lg:grid-cols-3 gap-6">
        {/* MAIN */}
        <div className="lg:col-span-2 space-y-6">
          {/* LIVE NOW */}
          <div className="kite-card p-5 border-[#00B4D8]/25" data-testid="spot-live-now">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-[#2EE59D]" /> {t('spotDetail.liveNow')}</h2>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="w-2 h-2 rounded-full bg-[#2EE59D] animate-pulse" /> {t('spotDetail.liveUpdated')}</span>
            </div>
            {nowWind ? (
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <div className="font-data text-5xl font-semibold text-[#00B4D8] leading-none">{nowVal}<span className="text-lg text-muted-foreground ml-1">{wu}</span></div>
                  <div className="text-xs text-muted-foreground mt-1">{t('spotDetail.gust')} {nowGust}{wu}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-[#48CAE4]" style={{ transform: `rotate(${(nowWind.dir_deg || 0)}deg)` }} />
                  <span className="font-data text-[#48CAE4]">{degToCompass(nowWind.dir_deg)}</span>
                </div>
                <span className={`ml-auto px-3 py-1.5 rounded-full text-xs font-semibold ${kiteable ? 'bg-[#2EE59D]/15 text-[#2EE59D]' : 'bg-[#FFB020]/15 text-[#FFB020]'}`} data-testid="spot-kiteable-badge">
                  {kiteable ? t('spotDetail.kiteable') : t('spotDetail.lightWind')}
                </span>
              </div>
            ) : (
              <div className="py-4 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            )}
            {nowWind && spot && (
              <SpotGearAdvice
                spot={spot}
                nowKn={nowKn}
                windDir={degToCompass(nowWind.dir_deg)}
                units={u}
                user={user}
              />
            )}
          </div>

          {/* WIND ALERT */}
          <WindAlertCard spot={spot} units={u} user={user} />

          {/* MY GEAR MATCH — which of the rider's own kites/boards fit */}
          <MyGearMatch spot={spot} forecast={forecast} nowKn={nowKn} units={u} user={user} />

          <div className="kite-card p-5">
            <p className="text-lg leading-relaxed text-white/85">{spot.description}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {bestMonthsLabels.length > 0 && (
                <span className="w-full text-sm text-muted-foreground mb-1">{t('spotDetail.bestSeason')}: <span className="text-[#FF6B35]">{bestMonthsLabels.join(', ')}</span></span>
              )}
              {(spot.wind_directions || []).map((d) => (
                <span key={d} className="flex items-center gap-1 text-xs font-data px-2.5 py-1 rounded-full bg-[#00B4D8]/12 text-[#00B4D8]"><Wind className="w-3 h-3" /> {d}</span>
              ))}
            </div>
          </div>

          {/* FORECAST */}
          <div className="kite-card p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#00B4D8]" /> {t('spotDetail.windForecast')}</h3>
              <span className="text-xs text-muted-foreground">{isPro ? t('spotDetail.forecastPro') : t('spotDetail.forecast5')}</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{t('spotDetail.next24')} · {wu === 'mph' ? 'mph' : t('spotDetail.knots')}</p>
            {hourly.length ? <WindForecastChart data={hourly} unit={wu} /> : <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
            {/* daily */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-5">
              {daily.map((d) => (
                <div key={d.date} className="rounded-xl bg-[#0A0A0A] border border-white/8 p-3 text-center">
                  <div className="text-xs text-muted-foreground">{new Date(d.date).toLocaleDateString(locale, { weekday: 'short' })}</div>
                  <div className="font-data text-2xl font-semibold text-[#00B4D8] mt-1">{displayWind(d.wind_max, u)}</div>
                  <div className="text-[10px] text-muted-foreground">{t('spotDetail.gust')} {displayWind(d.gust_max, u)}{wu}</div>
                  <div className="text-[11px] font-data text-[#48CAE4] mt-0.5">{d.wind_dir}</div>
                </div>
              ))}
            </div>
            {!isPro && (
              <button onClick={() => navigate('/pricing')} className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-[#FF6B35] border border-[#FF6B35]/40 rounded-xl py-2.5 hover:bg-[#FF6B35]/5" data-testid="spot-forecast-upgrade">
                <Lock className="w-4 h-4" /> {t('spotDetail.unlockForecast')}
              </button>
            )}
          </div>

          {/* HISTORY */}
          <div className="kite-card p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-1"><Sun className="w-4 h-4 text-[#FFB020]" /> {t('spotDetail.historicalWind')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{history?.source || t('spotDetail.avgMaxPerMonth')}</p>
            {history ? <>
              <HistoryChart data={historyMonthly} bestMonths={history.best_months} unit={wu} />
              <div className="mt-4 flex items-center gap-2 text-sm">
                <span className="label-eyebrow text-muted-foreground">{t('spotDetail.bestSeason')}</span>
                <span className="flex gap-1">{history.best_months_labels?.map((m) => <span key={m} className="px-2 py-0.5 rounded-md bg-[#FF6B35]/15 text-[#FF6B35] text-xs font-semibold">{m}</span>)}</span>
              </div>
            </> : <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          </div>
        </div>

        {/* SIDEBAR */}
        <div className="space-y-6">
          <div className="kite-card p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4 text-[#FFB020]" /> {t('spotDetail.hazards')}</h3>
            <ul className="space-y-2">{(spot.hazards || []).map((h, i) => <li key={`${i}-${h}`} className="text-sm text-white/80 flex items-start gap-2"><span className="text-[#FFB020] mt-1">•</span> {h}</li>)}</ul>
          </div>
          <div className="kite-card p-5">
            <h3 className="font-semibold mb-3">{t('spotDetail.facilities')}</h3>
            <div className="flex flex-wrap gap-2">{(spot.facilities || []).map((f) => <span key={f} className="text-xs px-2.5 py-1 rounded-full bg-[#0A0A0A] border border-white/10 text-muted-foreground">{f}</span>)}</div>
          </div>

          {/* rentals */}
          <div className="kite-card p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-3"><ShoppingBag className="w-4 h-4 text-[#48CAE4]" /> {t('spotDetail.gearRental')}</h3>
            <div className="space-y-3">
              {(spot.rentals || []).map((r, i) => (
                <div key={`${r.shop_name || 'rental'}-${i}`} className="rounded-xl bg-[#0A0A0A] border border-white/8 p-3">
                  <div className="font-medium text-sm">{r.shop_name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{t('spotDetail.fromPrice', { day: r.price_day, week: r.price_week })}</div>
                  <a href={r.affiliate_url} target="_blank" rel="noreferrer" className="text-xs text-[#48CAE4] hover:text-[#00B4D8] flex items-center gap-1 mt-1.5" data-testid="rental-affiliate-link">{t('spotDetail.viewRental')} <ExternalLink className="w-3 h-3" /></a>
                </div>
              ))}
            </div>
          </div>

          {/* accommodations */}
          <div className="kite-card p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-3"><Home className="w-4 h-4 text-[#FF6B35]" /> {t('spotDetail.kiteStays')}</h3>
            <div className="space-y-3">
              {(spot.accommodations || []).map((ac, i) => (
                <a key={`${ac.name || 'stay'}-${i}`} href={ac.affiliate_url} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden bg-[#0A0A0A] border border-white/8 hover:border-[#FF6B35]/40 transition-colors" data-testid="accommodation-affiliate-link">
                  <img src={ac.photo} alt={ac.name} className="w-full h-24 object-cover" />
                  <div className="p-3">
                    <div className="flex items-center justify-between"><span className="font-medium text-sm">{ac.name}</span><span className="text-xs text-[#FF6B35] font-data">{ac.price_range}</span></div>
                    <p className="text-xs text-muted-foreground mt-1">{ac.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* RELATED SPOTS (internal linking for SEO + discovery) */}
      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-12" data-testid="spot-related">
          <h2 className="font-display text-2xl mb-4">{t('spotDetail.relatedTitle')}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map((r) => (
              <Link key={r.id} to={`/spots/${r.id}`} className="group kite-card overflow-hidden hover:border-[#00B4D8]/40 transition-colors" data-testid="related-spot-card">
                <div className="relative h-28 overflow-hidden">
                  <img src={r.photos?.[0]} alt={`Kitesurfen in ${r.name}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                </div>
                <div className="p-3">
                  <div className="font-medium text-sm truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.country}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
